import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import { FaPrint, FaDownload } from "react-icons/fa";
import axiosInstance from "../config/axios";
import { buildCsv, downloadCsvFile } from "../utils/csvDownload";
import {
  COMMISSION_REPORT_CLASSIFICATIONS,
  COMMISSION_REPORT_SORT_OPTIONS,
  buildCommissionReport,
  formatAgentSortName,
  formatReportMoney,
} from "../utils/commissionReportUtils";
import logo from "../Assets/logo3.jpg";

const COMPANY = "ABC Realty Inc., Brokerage";
const REPORT_TITLE = "Trades - Commission Amount - Detailed";

const FinancialReportsCommission = () => {
  const { pathname } = useLocation();
  const isFinancePage = pathname === "/finance-commission-report";

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [agentId, setAgentId] = useState("");
  const [classification, setClassification] = useState("ALL");
  const [sortBy, setSortBy] = useState("offerDate");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [agentGroups, setAgentGroups] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const res = await axiosInstance.get("/agents");
        const sorted = (res.data || []).slice().sort((a, b) => {
          const empA = Number(a.employeeNo) || 0;
          const empB = Number(b.employeeNo) || 0;
          if (empA !== empB) return empA - empB;
          return formatAgentSortName(a).localeCompare(
            formatAgentSortName(b),
            undefined,
            { sensitivity: "base" },
          );
        });
        setAgents(sorted);
      } catch (err) {
        console.error(err);
      }
    };
    loadAgents();
  }, []);

  const selectedAgentLabel = () => {
    if (!agentId) return "All Agents";
    const a = agents.find((x) => String(x._id) === String(agentId));
    if (!a) return "All Agents";
    const emp = a.employeeNo != null ? `${a.employeeNo} ` : "";
    return `${emp}${formatAgentSortName(a)}`;
  };

  const classificationLabel = () => {
    const opt = COMMISSION_REPORT_CLASSIFICATIONS.find(
      (o) => o.value === classification,
    );
    return opt?.label || classification;
  };

  const sortByLabel = () => {
    const opt = COMMISSION_REPORT_SORT_OPTIONS.find((o) => o.value === sortBy);
    return opt?.label || sortBy;
  };

  const formatPeriod = () => {
    if (!fromDate || !toDate) return "";
    const fmt = (iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${months[m - 1]} ${String(d).padStart(2, "0")}, ${y}`;
    };
    return `From ${fmt(fromDate)} To ${fmt(toDate)}`;
  };

  const handleGenerate = async () => {
    if (!fromDate || !toDate) {
      setError("Please select both Start and End dates.");
      return;
    }
    if (fromDate > toDate) {
      setError("Start date must be on or before End date.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/trades");
      const trades = res.data || [];
      const result = buildCommissionReport(trades, agents, {
        fromDate,
        toDate,
        agentId,
        classification,
        sortBy,
      });
      setAgentGroups(result.agentGroups);
      setSummary(result.summary);
      setShowReport(true);
    } catch (err) {
      console.error(err);
      setError("Failed to load trades.");
      setAgentGroups([]);
      setSummary(null);
      setShowReport(false);
    } finally {
      setLoading(false);
    }
  };

  const openPrintWindow = () => {
    if (!showReport) return;

    const safe = (v) =>
      String(v ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;");

    const money = (n) => formatReportMoney(n);

    const groupsHtml =
      agentGroups.length === 0
        ? `<tr><td colspan="11" style="text-align:center;padding:8px;">No trades for this selection.</td></tr>`
        : agentGroups
            .map((g) => {
              const header = `
              <tr class="agent-hdr">
                <td colspan="11">${safe(g.groupLabel)}</td>
              </tr>`;
              const rows = g.rows
                .map(
                  (r) => `
                <tr>
                  <td>${safe(r.tradeNumber)}</td>
                  <td>${safe(r.address)}</td>
                  <td class="ctr">${safe(r.classCode)}</td>
                  <td>${safe(r.offerDate)}</td>
                  <td>${safe(r.closeDate)}</td>
                  <td class="num">${r.comPercent != null ? Number(r.comPercent).toFixed(2) : ""}</td>
                  <td class="num">${money(r.commissionDeal)}</td>
                  <td class="num">${money(r.commissionBalance)}</td>
                  <td class="num">${money(r.netDeal)}</td>
                  <td class="num">${money(r.netBalance)}</td>
                  <td>${safe(r.planInfo)}${r.deductions ? `<div class="ded">Ded: ${money(r.deductions)}</div>` : ""}</td>
                </tr>`,
                )
                .join("");
              const sub = `
              <tr class="subtot">
                <td colspan="2">Trade Count: ${g.tradeCount.toFixed(2)}</td>
                <td colspan="4">End Count: ${g.endCount.toFixed(2)}</td>
                <td></td>
                <td class="num">${money(g.commissionTotal)}</td>
                <td></td>
                <td class="num">${money(g.netTotal)}</td>
                <td></td>
              </tr>`;
              return header + rows + sub;
            })
            .join("");

    const totalsHtml = summary
      ? `
        <tr class="grand">
          <td colspan="2">Total Trade Count: ${summary.tradeCount.toFixed(2)}</td>
          <td colspan="4">Total End Count: ${summary.endCount.toFixed(2)}</td>
          <td></td>
          <td class="num">${money(summary.commissionGrand)}</td>
          <td></td>
          <td class="num">${money(summary.netGrand)}</td>
          <td></td>
        </tr>
        <tr class="grand">
          <td colspan="7">Total Deposit Amount</td>
          <td class="num" colspan="4">${money(summary.totalDepositAmount)}</td>
        </tr>
        ${
          summary.deductionsGrand
            ? `<tr class="grand">
          <td colspan="7">Total Deductions</td>
          <td class="num" colspan="4">${money(summary.deductionsGrand)}</td>
        </tr>`
            : ""
        }
      `
      : "";

    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;

    w.document.write(`
      <html>
        <head>
          <title></title>
          <style>
            @page { size: portrait; margin: 0; }
            html, body { margin: 0; padding: 0; }
            body {
              font-family: Arial, sans-serif;
              font-size: 9px;
              color: #111;
              padding: 12mm;
              box-sizing: border-box;
            }
            .header { text-align: center; margin-bottom: 12px; }
            .header .company { font-weight: 700; font-size: 12px; }
            .header .title { font-weight: 700; font-size: 12px; margin-top: 2px; }
            .header .meta { font-size: 9px; margin-top: 2px; }
            .logo { max-height: 40px; display:block; margin: 0 auto 6px; }
            table { width: 100%; border-collapse: collapse; font-size: 8px; }
            th, td { border: 1px solid #999; padding: 2px 3px; vertical-align: top; }
            th { background: #f2f2f2; text-align: center; font-size: 7.5px; }
            td.num, th.num { text-align: right; white-space: nowrap; }
            td.ctr { text-align: center; }
            tr.agent-hdr td { font-weight: 700; background: #eef2f7; }
            tr.subtot td { font-weight: 600; background: #fafafa; }
            tr.grand td { font-weight: 700; }
            .ded { color: #b91c1c; font-size: 7.5px; }
            .print-btn { margin-top: 12px; display:block; margin-left:auto; margin-right:auto; }
            @media print {
              .print-btn { display:none !important; }
              body { padding: 12mm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img class="logo" src="${logo}" alt="" />
            <div class="company">${safe(COMPANY)}</div>
            <div class="title">${safe(REPORT_TITLE)} - ${safe(selectedAgentLabel())}</div>
            <div class="meta">Include All Fees</div>
            <div class="meta">${safe(formatPeriod())}</div>
            <div class="meta">Classification: ${safe(classificationLabel())} · Sorted by: ${safe(sortByLabel())}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Trade #</th>
                <th>Address</th>
                <th>Class.</th>
                <th>Offer Date</th>
                <th>Close Date</th>
                <th>Com. %</th>
                <th colspan="2">Commission<br/>Deal $ &nbsp; Balance $</th>
                <th colspan="2">Net<br/>Deal $ &nbsp; Balance $</th>
                <th>Plan Info / Deductions</th>
              </tr>
              <tr>
                <th></th><th></th><th></th><th></th><th></th><th></th>
                <th class="num">Deal $</th>
                <th class="num">Balance $</th>
                <th class="num">Deal $</th>
                <th class="num">Balance $</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${groupsHtml}
              ${totalsHtml}
            </tbody>
          </table>
          <button class="print-btn" onclick="window.print()">Print</button>
        </body>
      </html>
    `);
    w.document.close();
    try {
      w.document.title = " ";
    } catch (_) {
      /* ignore */
    }
    w.focus();
  };

  const handleDownloadCsv = () => {
    if (!agentGroups.length) return;
    const headers = [
      "Agent",
      "Trade #",
      "Address",
      "Class.",
      "Offer Date",
      "Close Date",
      "Com. %",
      "Commission Deal $",
      "Commission Balance $",
      "Net Deal $",
      "Net Balance $",
      "Plan Info",
      "Deductions",
    ];
    const rows = [];
    for (const g of agentGroups) {
      for (const r of g.rows) {
        rows.push([
          g.groupLabel,
          r.tradeNumber,
          r.address,
          r.classCode,
          r.offerDate,
          r.closeDate,
          r.comPercent != null ? Number(r.comPercent).toFixed(2) : "",
          Number(r.commissionDeal || 0).toFixed(2),
          Number(r.commissionBalance || 0).toFixed(2),
          Number(r.netDeal || 0).toFixed(2),
          Number(r.netBalance || 0).toFixed(2),
          r.planInfo,
          Number(r.deductions || 0).toFixed(2),
        ]);
      }
    }
    if (summary) {
      rows.push([]);
      rows.push([
        "TOTALS",
        `Trades: ${summary.tradeCount}`,
        `Ends: ${summary.endCount}`,
        "",
        "",
        "",
        "",
        Number(summary.commissionGrand || 0).toFixed(2),
        "",
        Number(summary.netGrand || 0).toFixed(2),
        "",
        "Total Deposit",
        Number(summary.totalDepositAmount || 0).toFixed(2),
      ]);
      rows.push([
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "Total Deductions",
        Number(summary.deductionsGrand || 0).toFixed(2),
      ]);
    }
    const csv = buildCsv(headers, rows);
    downloadCsvFile(`commission-report-${fromDate}-to-${toDate}.csv`, csv);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex flex-1">
        <div className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center mb-6 no-print">
              <h1 className="text-2xl font-bold text-gray-900">
                Commission Report
              </h1>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6 no-print">
              <p className="text-sm text-gray-600 mb-4">
                Trades commission amount (detailed), with plan info and
                deductions. Date range uses the selected Sort By field.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agent Name
                  </label>
                  <select
                    value={agentId}
                    onChange={(e) => setAgentId(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  >
                    <option value="">All Agents</option>
                    {agents.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.employeeNo != null ? `${a.employeeNo} — ` : ""}
                        {formatAgentSortName(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Classification
                  </label>
                  <select
                    value={classification}
                    onChange={(e) => setClassification(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  >
                    {COMMISSION_REPORT_CLASSIFICATIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  >
                    {COMMISSION_REPORT_SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
                  >
                    {loading ? "Loading…" : "Generate Report"}
                  </button>
                  {showReport && agentGroups.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={openPrintWindow}
                        className="flex items-center gap-2 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                      >
                        <FaPrint /> Print
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadCsv}
                        className="flex items-center gap-2 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                      >
                        <FaDownload /> CSV
                      </button>
                    </>
                  )}
                </div>
              </div>
              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>

            {showReport && (
              <div className="bg-white p-6 rounded-lg shadow-md overflow-x-auto">
                {agentGroups.length === 0 ? (
                  <p className="text-gray-600">
                    No trades found for this selection.
                  </p>
                ) : (
                  <table className="min-w-full border border-gray-300 text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-2 py-1 text-left">Trade #</th>
                        <th className="border px-2 py-1 text-left">Address</th>
                        <th className="border px-2 py-1 text-center">Class.</th>
                        <th className="border px-2 py-1 text-left">
                          Offer Date
                        </th>
                        <th className="border px-2 py-1 text-left">
                          Close Date
                        </th>
                        <th className="border px-2 py-1 text-right">Com. %</th>
                        <th className="border px-2 py-1 text-right">
                          Comm. Deal $
                        </th>
                        <th className="border px-2 py-1 text-right">
                          Comm. Balance $
                        </th>
                        <th className="border px-2 py-1 text-right">
                          Net Deal $
                        </th>
                        <th className="border px-2 py-1 text-right">
                          Net Balance $
                        </th>
                        <th className="border px-2 py-1 text-left">Plan Info</th>
                        <th className="border px-2 py-1 text-right">
                          Deductions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentGroups.map((g) => (
                        <React.Fragment key={g.groupKey}>
                          <tr className="bg-blue-50">
                            <td
                              colSpan={12}
                              className="border px-2 py-1 font-semibold"
                            >
                              {g.groupLabel}
                            </td>
                          </tr>
                          {g.rows.map((r, idx) => (
                            <tr key={`${g.groupKey}-${r.tradeNumber}-${idx}`}>
                              <td className="border px-2 py-1">
                                {r.tradeNumber}
                              </td>
                              <td className="border px-2 py-1">{r.address}</td>
                              <td className="border px-2 py-1 text-center">
                                {r.classCode}
                              </td>
                              <td className="border px-2 py-1">
                                {r.offerDate}
                              </td>
                              <td className="border px-2 py-1">
                                {r.closeDate}
                              </td>
                              <td className="border px-2 py-1 text-right">
                                {r.comPercent != null
                                  ? Number(r.comPercent).toFixed(2)
                                  : ""}
                              </td>
                              <td className="border px-2 py-1 text-right">
                                {formatReportMoney(r.commissionDeal)}
                              </td>
                              <td className="border px-2 py-1 text-right">
                                {formatReportMoney(r.commissionBalance)}
                              </td>
                              <td className="border px-2 py-1 text-right">
                                {formatReportMoney(r.netDeal)}
                              </td>
                              <td className="border px-2 py-1 text-right">
                                {formatReportMoney(r.netBalance)}
                              </td>
                              <td className="border px-2 py-1">
                                {r.planInfo || "—"}
                              </td>
                              <td className="border px-2 py-1 text-right">
                                {r.deductions
                                  ? formatReportMoney(r.deductions)
                                  : ""}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-gray-50 font-medium">
                            <td className="border px-2 py-1" colSpan={2}>
                              Trade Count: {g.tradeCount.toFixed(2)}
                            </td>
                            <td className="border px-2 py-1" colSpan={4}>
                              End Count: {g.endCount.toFixed(2)}
                            </td>
                            <td className="border px-2 py-1" />
                            <td className="border px-2 py-1 text-right">
                              {formatReportMoney(g.commissionTotal)}
                            </td>
                            <td className="border px-2 py-1" />
                            <td className="border px-2 py-1 text-right">
                              {formatReportMoney(g.netTotal)}
                            </td>
                            <td className="border px-2 py-1" colSpan={2} />
                          </tr>
                        </React.Fragment>
                      ))}
                      {summary && (
                        <>
                          <tr className="font-bold bg-gray-100">
                            <td className="border px-2 py-2" colSpan={2}>
                              Total Trade Count:{" "}
                              {summary.tradeCount.toFixed(2)}
                            </td>
                            <td className="border px-2 py-2" colSpan={4}>
                              Total End Count: {summary.endCount.toFixed(2)}
                            </td>
                            <td className="border px-2 py-2" />
                            <td className="border px-2 py-2 text-right">
                              {formatReportMoney(summary.commissionGrand)}
                            </td>
                            <td className="border px-2 py-2" />
                            <td className="border px-2 py-2 text-right">
                              {formatReportMoney(summary.netGrand)}
                            </td>
                            <td className="border px-2 py-2" colSpan={2} />
                          </tr>
                          <tr className="font-bold bg-gray-100">
                            <td className="border px-2 py-2" colSpan={7}>
                              Total Deposit Amount
                            </td>
                            <td
                              className="border px-2 py-2 text-right"
                              colSpan={5}
                            >
                              {formatReportMoney(summary.totalDepositAmount)}
                            </td>
                          </tr>
                          {summary.deductionsGrand > 0 && (
                            <tr className="font-semibold bg-gray-50">
                              <td className="border px-2 py-2" colSpan={7}>
                                Total Deductions
                              </td>
                              <td
                                className="border px-2 py-2 text-right"
                                colSpan={5}
                              >
                                {formatReportMoney(summary.deductionsGrand)}
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialReportsCommission;
